import i18n from '@/utils/i18n';
import moment from 'moment';
import React, { useMemo } from 'react';
import { Badge } from '@usepepper/ui/atoms/badge';
import { createStyles } from '@/contexts/theme-context';
import { formatCurrency } from '@/utils/money';
import {
  InvoiceUnpaidItemType,
  UnpaidItemType,
} from '@/contexts/payments-context';
import { PaymentItemType } from '@/enums';
import { Typography } from '@usepepper/ui/atoms/typography';
import { View } from 'react-native';

interface Props {
  currencyCode: string | null;
  items: UnpaidItemType[];
}

export const UnpaidInvoiceTotalComponent: React.FC<Props> = ({
  currencyCode,
  items,
}) => {
  const [styles, theme] = useStyles();

  // Can be negative when credit memos exceed invoice totals.
  const totalInMicros = useMemo(
    () => items.reduce((sum, item) => sum + item.amountInMicros, 0),
    [items],
  );

  const overdueInMicros = useMemo(
    () =>
      items
        .filter(
          (item): item is InvoiceUnpaidItemType =>
            item.type === PaymentItemType.Invoice &&
            item.dueAt !== null &&
            item.dueAt < moment().startOf('day').toDate(),
        )
        .reduce((sum, item) => sum + item.amountInMicros, 0),
    [items],
  );

  // Capped at the total balance so credit memos don't cause the overdue
  // amount to exceed what is actually owed.
  const cappedOverdueInMicros = Math.min(
    overdueInMicros,
    Math.max(totalInMicros, 0),
  );

  return (
    <View style={styles.base}>
      <Typography variant="heading-2xl">
        {formatCurrency(
          totalInMicros,
          currencyCode,
          totalInMicros === 0 ? { minimumFractionDigits: 0 } : undefined,
        )}
      </Typography>
      {cappedOverdueInMicros > 0 ? (
        <View style={styles.overdueRow}>
          <Typography
            color={theme.sk.color.text.error}
            variant="paragraph-base"
          >
            {formatCurrency(cappedOverdueInMicros, currencyCode)}
          </Typography>
          <Badge
            alignment="center"
            label={i18n.t('overdue')}
            size="small"
            variant="error"
          />
        </View>
      ) : null}
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    flexDirection: 'column',
    gap: theme.sk.spacing.spaceXsmall,
  },
  overdueRow: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSmall,
  },
}));
